/**
 * Alexia Shop - API Client
 * =========================
 * Axios-based API client for backend communication.
 */

import axios, { type AxiosInstance, type AxiosError, type AxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session auth
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Get CSRF token from cookie if exists
    const csrfToken = getCookie('csrf_token')
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken
    }

    // Add language header (i18n uses 'locale' key)
    const language = localStorage.getItem('locale') || 'es'
    config.headers['Accept-Language'] = language

    // Add lang query param for APIs that need it
    const url = new URL(config.url || '', 'http://localhost')
    if (!url.searchParams.has('lang')) {
      const separator = config.url?.includes('?') ? '&' : '?'
      config.url = `${config.url}${separator}lang=${language}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError<ApiResponse<unknown>>) => {
    // Handle common errors
    if (error.response) {
      const { status, data, config } = error.response
      const url = config?.url || ''

      switch (status) {
        case 401:
          // Don't redirect for auth-checking endpoints or admin API calls
          // Let the router guard handle admin route authentication
          if (!url.includes('/auth/') && !url.includes('/admin/')) {
            // Unauthorized on non-admin routes - redirect to login
            window.location.href = '/auth/login'
          }
          break
        case 403:
          // Forbidden
          console.error('Access denied:', data?.message)
          break
        case 404:
          // Not found
          console.error('Resource not found:', data?.message)
          break
        case 422:
          // Validation error - let the caller handle it
          break
        case 500:
          // Server error
          console.error('Server error:', data?.message)
          break
      }
    } else if (error.request) {
      // Network error
      console.error('Network error - please check your connection')
    }

    return Promise.reject(error)
  }
)

// Helper to get cookie value
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  return null
}

// Export typed API methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<ApiResponse<T>>(url, config),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<ApiResponse<T>>(url, data, config),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<ApiResponse<T>>(url, data, config),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<ApiResponse<T>>(url, data, config),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<ApiResponse<T>>(url, config),
}

export default apiClient
